import { maskWidgetSecret } from '../utils/widgetChannel';

type WidgetSecretCredentialsProps = {
  /** Saved secret from server (full value — only shown masked). */
  storedSecret: string | null;
  /** Newly generated secret not yet saved (optional — shown masked until save). */
  draftSecret: string | null;
  saving: boolean;
  onGenerate: () => void;
  onRegenerate: () => void;
  onCopySecret: () => void;
  onCopyJson: () => void;
};

export function WidgetSecretCredentials({
  storedSecret,
  draftSecret,
  saving,
  onGenerate,
  onRegenerate,
  onCopySecret,
  onCopyJson,
}: WidgetSecretCredentialsProps) {
  const activeSecret = draftSecret ?? storedSecret;
  const hasStored = Boolean(storedSecret);
  const hasDraft = Boolean(draftSecret);
  const masked = activeSecret ? maskWidgetSecret(activeSecret) : null;

  return (
    <div className="widget-secret-block">
      <p className="text-muted widget-secret-help">
        Host apps (Garages, FLOWW) sign short-lived JWTs with this secret. The{' '}
        <strong>identifier</strong> above must match JWT <code>source</code> (e.g.{' '}
        <code>GARAGES</code>, <code>FLOWW</code>) — not a URL.
      </p>

      {activeSecret ? (
        <div className="widget-secret-row">
          <label className="widget-secret-label" htmlFor="widget-secret-display">
            widgetSecret
          </label>
          <div className="widget-secret-value-row">
            <code id="widget-secret-display" className="widget-secret-masked">
              {masked}
            </code>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={saving}
              onClick={onCopySecret}
            >
              Copy secret
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={saving}
              onClick={onCopyJson}
            >
              Copy JSON
            </button>
          </div>
          {hasDraft && (
            <p className="widget-secret-draft-note">
              New secret generated — click <strong>Save changes</strong> to apply. Until then,
              widget-exchange still uses the previous secret.
            </p>
          )}
        </div>
      ) : (
        <p className="text-muted widget-secret-empty">No widget secret configured yet.</p>
      )}

      <div className="widget-secret-actions">
        {!hasStored && !hasDraft ? (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={saving}
            onClick={onGenerate}
          >
            Generate widget secret
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={saving}
            onClick={onRegenerate}
          >
            Regenerate widget secret
          </button>
        )}
      </div>
    </div>
  );
}
